import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #lastSelectedOptionId;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    // OPTIMIZATION (shade is now a separate product, not a variant option):
    // Recommendation sync was built to mirror a "Shade" option selection across
    // recommendation swatch pickers. Since Shade no longer exists as an option
    // on this component, this listener has nothing left to sync. Commented out
    // rather than removed in case a future option (e.g. Size) needs this again.
    // this.addEventListener('variantRecommendationSync', this.handleRecommendationSync.bind(this));

    const initialSelected = this.selectedOption;
    if (initialSelected && initialSelected.dataset.optionValueId) {
      this.#lastSelectedOptionId = initialSelected.dataset.optionValueId;
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.target.dataset.optionValueId) {
      this.#lastSelectedOptionId = event.target.dataset.optionValueId;
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }

    // OPTIMIZATION (shade is now a separate product, not a variant option):
    // This used to push the selected Shade to recommendation swatch pickers so
    // related products previewed the same color. Shade selection now happens by
    // navigating to a different (color) product entirely, so there is no shade
    // option here to sync. Commented out rather than removed.
    // if (isOnProductPage) {
    //   this.syncRecommendationVariants(event.target);
    // }
  }

  // ===== OPTIMIZATION: shade is now a separate product, not a variant option =====
  // The methods below (syncRecommendationVariants ... showSyncAnimation) existed to
  // mirror a "Shade" option selection across recommendation swatch pickers on other
  // product cards. Since color/shade is no longer an option rendered by this component
  // (each shade is its own linked product now), there is nothing for this sync logic to
  // match against, so it's dead code in the new architecture. Commented out rather than
  // deleted in case a future option (e.g. Size) needs cross-recommendation syncing.
  // /**
  // * Syncs variant selections to recommendation products with proper re-rendering
  // * @param {HTMLElement} selectedOption - The selected option element
  // */
  // syncRecommendationVariants(selectedOption) {
  // const optionName = this.getOptionName(selectedOption);
  // const optionValue = this.getOptionValue(selectedOption);

  // if (!optionName || !optionValue) {
  // console.warn('Could not determine option name or value for syncing');
  // return;
  // }

  // // Find all recommendation swatch variant pickers (the actual structure)
  // const recommendationSwatchPickers = document.querySelectorAll('product-recommendations.change-product-on-variant-select swatches-variant-picker-component');

  // if (recommendationSwatchPickers.length === 0) {
  // console.log('No recommendation swatch variant pickers found to sync');
  // return;
  // }

  // console.log(`Syncing ${optionName}: ${optionValue} to ${recommendationSwatchPickers.length} recommendations`);

  // // Process each recommendation swatch picker
  // recommendationSwatchPickers.forEach(swatchPicker => {
  // this.syncSingleRecommendationSwatchPicker(swatchPicker, optionName, optionValue);
  // });
  // }

  // /**
  // * Syncs a single recommendation swatch variant picker
  // * @param {Element} swatchPicker - The swatch variant picker element
  // * @param {string} optionName - The option name to sync (e.g., "Shade")
  // * @param {string} optionValue - The option value to sync (e.g., "Leche")
  // */
  // syncSingleRecommendationSwatchPicker(swatchPicker, optionName, optionValue) {
  // try {
  // if (!(swatchPicker instanceof HTMLElement)) {
  // console.warn('Invalid swatch picker element');
  // return;
  // }
  // // Get the product ID from data attributes
  // const productId = swatchPicker.dataset.productId;
  // if (!productId) {
  // console.warn('No product ID found for recommendation swatch picker');
  // return;
  // }

  // // Find the matching option in the swatch picker by value
  // const matchingInput = swatchPicker.querySelector(`input[value="${optionValue}"]`);

  // if (!matchingInput) {
  // console.log(`No matching option "${optionValue}" found in recommendation product ${productId}`);
  // return;
  // }

  // // Check if option is available
  // if (!this.isOptionAvailable(matchingInput)) {
  // console.log(`Option ${optionValue} is not available in recommendation product ${productId}`);
  // return;
  // }

  // // Only proceed if not already selected
  // if (matchingInput.checked) {
  // console.log(`Option ${optionValue} already selected in recommendation product ${productId}`);
  // return;
  // }

  // // Select the matching option
  // this.selectRecommendationSwatchOption(swatchPicker, matchingInput, optionName, optionValue);

  // console.log(`Successfully synced ${optionName}: ${optionValue} to recommendation product ${productId}`);

  // } catch (error) {
  // console.error('Error syncing recommendation swatch picker:', error);
  // }
  // }



  // /**
  // * Handles recommendation sync events
  // * @param {CustomEvent} event - The sync event
  // */
  // handleRecommendationSync(event) {
  // const { detail } = event;
  // console.log('Recommendation variant synced:', detail);

  // // Update any visual indicators or perform additional actions
  // this.updateRecommendationSyncIndicator(detail);

  // // Optionally update URL or perform other side effects
  // if (detail.variantId) {
  // this.handleRecommendationVariantUpdate(detail);
  // }
  // }

  // /**
  // * Updates sync indicators (optional visual feedback)
  // * @param {Object} detail - The sync event detail
  // */
  // updateRecommendationSyncIndicator(detail) {
  // // Add visual feedback that sync occurred
  // const picker = detail.picker;
  // if (picker) {
  // picker.classList.add('sync-updated');

  // // Remove indicator after animation
  // setTimeout(() => {
  // picker.classList.remove('sync-updated');
  // }, 300);
  // }
  // }

  // /**
  // * Handles recommendation variant updates
  // * @param {Object} detail - The sync event detail
  // */
  // handleRecommendationVariantUpdate(detail) {
  // // You can add additional logic here such as:
  // // - Updating recommendation product images
  // // - Updating prices
  // // - Triggering analytics events
  // // - etc.
  // console.log(`Recommendation variant updated to: ${detail.variantId}`);
  // }

  // /**
  // * Gets the option name from a selected element
  // * @param {HTMLElement} selectedOption - The selected option element
  // * @returns {string|null} The option name
  // */
  // getOptionName(selectedOption) {
  // if (selectedOption instanceof HTMLInputElement && selectedOption.type === 'radio') {
  // return selectedOption.name.replace('options[', '').replace(']', '');
  // }

  // if (selectedOption instanceof HTMLSelectElement) {
  // return selectedOption.name.replace('options[', '').replace(']', '');
  // }

  // if (selectedOption instanceof HTMLOptionElement) {
  // const select = selectedOption.closest('select');
  // return select ? select.name.replace('options[', '').replace(']', '') : null;
  // }

  // return null;
  // }

  // /**
  // * Gets the option value from a selected element
  // * @param {HTMLElement} selectedOption - The selected option element
  // * @returns {string|null} The option value
  // */
  // getOptionValue(selectedOption) {
  // if (selectedOption instanceof HTMLInputElement) {
  // return selectedOption.value;
  // }

  // if (selectedOption instanceof HTMLSelectElement) {
  // return selectedOption.value;
  // }

  // if (selectedOption instanceof HTMLOptionElement) {
  // return selectedOption.value;
  // }

  // return null;
  // }

  // /**
  // * Checks if an option is available
  // * @param {HTMLElement} option - The option element to check
  // * @returns {boolean} Whether the option is available
  // */
  // isOptionAvailable(option) {
  // if (option instanceof HTMLInputElement) {
  // return option.dataset.optionAvailable !== 'false' && !option.disabled;
  // }

  // if (option instanceof HTMLOptionElement) {
  // return !option.disabled;
  // }

  // return true;
  // }

  // /**
  // * Selects an option in a recommendation swatch variant picker
  // * @param {Element} swatchPicker - The swatch variant picker element
  // * @param {HTMLInputElement} optionInput - The input option to select
  // * @param {string} optionName - The option name
  // * @param {string} optionValue - The option value
  // */
  // selectRecommendationSwatchOption(swatchPicker, optionInput, optionName, optionValue) {
  // try {
  // // Uncheck all radio buttons in the same group
  // const radioGroup = swatchPicker.querySelectorAll(`input[name="${optionInput.name}"]`);
  // radioGroup.forEach(radio => {
  // if (radio instanceof HTMLInputElement) {
  // radio.checked = false;
  // // Remove selected visual state from labels
  // const label = swatchPicker.querySelector(`label[for="${radio.id}"]`);
  // if (label) {
  // label.classList.remove('selected', 'active');
  // }
  // }
  // });

  // // Select the new option
  // optionInput.checked = true;

  // // Add selected visual state to the new label
  // const selectedLabel = swatchPicker.querySelector(`label[for="${optionInput.id}"]`) ||
  // optionInput.closest('label');
  // if (selectedLabel) {
  // selectedLabel.classList.add('selected', 'active');
  // }

  // // Update product images if media-id is available
  // const mediaId = optionInput.dataset.optionMediaId;
  // if (mediaId) {
  // this.updateRecommendationProductImages(swatchPicker, mediaId);
  // }

  // // Trigger change event for any listeners
  // const changeEvent = new Event('change', { bubbles: true });
  // optionInput.dispatchEvent(changeEvent);

  // // Update variant price and data
  // this.updateRecommendationVariantData(swatchPicker, optionInput);

  // // Visual feedback for sync
  // this.showSyncAnimation(swatchPicker);

  // } catch (error) {
  // console.error('Error selecting recommendation swatch option:', error);
  // }
  // }

  // /**
  // * Updates product images for recommendation products
  // * @param {Element} swatchPicker - The swatch variant picker
  // * @param {string} mediaId - The media ID to show
  // */
  // updateRecommendationProductImages(swatchPicker, mediaId) {
  // try {
  // const productCard = swatchPicker.closest('product-card');
  // if (!productCard) return;

  // // Find the slideshow component
  // const slideshow = productCard.querySelector('slideshow-component');
  // if (!slideshow) return;

  // // Find the slide with matching media ID
  // const targetSlide = slideshow.querySelector(`slideshow-slide[slide-id="${mediaId}"]`);
  // if (!targetSlide) return;

  // // Hide all slides
  // const allSlides = slideshow.querySelectorAll('slideshow-slide');
  // allSlides.forEach(slide => {
  // slide.setAttribute('aria-hidden', 'true');
  // });

  // // Show the target slide
  // targetSlide.setAttribute('aria-hidden', 'false');

  // // Update the slideshow to show the correct slide
  // const slides = Array.from(allSlides);
  // const slideIndex = slides.indexOf(targetSlide);
  // if (slideIndex >= 0) {
  // // Set initial-slide attribute if available
  // slideshow.setAttribute('initial-slide', slideIndex.toString());
  // }

  // } catch (error) {
  // console.error('Error updating recommendation product images:', error);
  // }
  // }

  // /**
  // * Updates variant data for recommendation product
  // * @param {Element} swatchPicker - The swatch variant picker
  // * @param {HTMLInputElement} optionInput - The selected input
  // */
  // updateRecommendationVariantData(swatchPicker, optionInput) {
  // try {
  // const variantId = optionInput.dataset.variantId;
  // if (!variantId) return;

  // // Find and update hidden variant input if it exists
  // const productCard = swatchPicker.closest('product-card');
  // if (productCard) {
  // const hiddenVariantInput = productCard.querySelector('input[name="id"]');
  // if (hiddenVariantInput) {
  // if (!(hiddenVariantInput instanceof HTMLInputElement)) return;
  // hiddenVariantInput.value = variantId;
  // }
  // }
  // if (!(swatchPicker instanceof HTMLInputElement)) return;

  // // Update any data attributes that might be needed
  // swatchPicker.dataset.selectedVariantId = variantId;

  // // Trigger custom event for other components that might need to update
  // const variantUpdateEvent = new CustomEvent('recommendationVariantUpdate', {
  // bubbles: true,
  // detail: {
  // variantId: variantId,
  // optionValue: optionInput.value,
  // picker: swatchPicker
  // }
  // });

  // swatchPicker.dispatchEvent(variantUpdateEvent);

  // } catch (error) {
  // console.error('Error updating recommendation variant data:', error);
  // }
  // }

  // /**
  // * Shows sync animation feedback
  // * @param {Element} swatchPicker - The swatch variant picker
  // */
  // showSyncAnimation(swatchPicker) {
  // try {
  // const productCard = swatchPicker.closest('product-card');
  // if (productCard) {
  // productCard.classList.add('sync-updated');

  // // Remove animation class after completion
  // setTimeout(() => {
  // productCard.classList.remove('sync-updated');
  // }, 600);
  // }
  // } catch (error) {
  // console.error('Error showing sync animation:', error);
  // }
  // }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      if (target.type === 'radio') {
        const radioGroup = this.querySelectorAll(`input[name="${target.name}"]`);
        radioGroup.forEach(radio => {
          if (radio instanceof HTMLInputElement) {
            radio.checked = false;
          }
        });
      }

      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          this.updateProductImages(html);
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    const selectedBeforeMorph = this.#lastSelectedOptionId;

    const isSwatchesComponent = this.closest('swatches-variant-picker-component');

    if (isSwatchesComponent && selectedBeforeMorph) {
      this.updateSwatchesSelectively(newVariantPickerSource, selectedBeforeMorph);
    } else {
      morph(this, newVariantPickerSource);
      if (selectedBeforeMorph) {
        this.restoreSelection(selectedBeforeMorph);
      }
    }

    return newProduct;
  }

  /**
   * Updates swatches component selectively to preserve selection
   * @param {Element} newVariantPickerSource - The new variant picker element
   * @param {string} selectedOptionId - The currently selected option ID
   */
  updateSwatchesSelectively(newVariantPickerSource, selectedOptionId) {
    const newScript = newVariantPickerSource.querySelector('script[type="application/json"]:not([data-metafield-variant])');
    const currentScript = this.querySelector('script[type="application/json"]:not([data-metafield-variant])');

    if (newScript && currentScript) {
      currentScript.textContent = newScript.textContent;
    }

    const newMetafieldScript = newVariantPickerSource.querySelector('script[data-metafield-variant]');
    const currentMetafieldScript = this.querySelector('script[data-metafield-variant]');

    if (newMetafieldScript && currentMetafieldScript) {
      currentMetafieldScript.textContent = newMetafieldScript.textContent;
    } else if (newMetafieldScript && !currentMetafieldScript) {
      const form = this.querySelector('form');
      if (!form) {
        throw new Error('No form found to append metafield script');
      }
      form.appendChild(newMetafieldScript.cloneNode(true));
    }

    const newOptions = newVariantPickerSource.querySelectorAll('input[data-option-value-id]');
    newOptions.forEach(newOption => {
      if (!(newOption instanceof HTMLInputElement)) return;
      const optionId = newOption.dataset.optionValueId;
      const currentOption = this.querySelector(`input[data-option-value-id="${optionId}"]`);
      if (!currentOption || !(currentOption instanceof HTMLInputElement)) return;

      if (currentOption) {
        currentOption.dataset.optionAvailable = newOption.dataset.optionAvailable;
        if (newOption.dataset.variantId) {
          currentOption.dataset.variantId = newOption.dataset.variantId;
        }
        if (optionId !== selectedOptionId) {
          currentOption.checked = false;
        } else {
          currentOption.checked = true;
        }
      }
    });

    const newStrikethroughs = newVariantPickerSource.querySelectorAll('[data-option-value-id] + * [class*="strikethrough"], [data-option-value-id] ~ * [class*="strikethrough"]');
    newStrikethroughs.forEach(newStrike => {
      if (!(newStrike instanceof HTMLElement || !newStrike)) return;
      if (!(newStrike instanceof HTMLElement)) return;

      const liElement = newStrike.closest('li');
      if (!liElement) return;

      const optionElement = liElement.querySelector('[data-option-value-id]');
      if (!optionElement) return;

      const optionId = optionElement instanceof HTMLElement ? optionElement.dataset.optionValueId : null;
      if (optionId) {
        const currentStrike = this.querySelector(`[data-option-value-id="${optionId}"] + *, [data-option-value-id="${optionId}"] ~ *`);
        if (currentStrike) {
          // Update strikethrough element
          const currentStrikeElement = currentStrike.querySelector('[class*="strikethrough"]');
          const newStrikeElement = newStrike;
          if (currentStrikeElement && newStrikeElement) {
            morph(currentStrikeElement, newStrikeElement);
          }
        }
      }
    });
  }

  /**
   * Restores the selection after morphing
   * @param {string} optionValueId - The option value ID to restore
   */
  restoreSelection(optionValueId) {
    const targetElement = this.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (targetElement instanceof HTMLInputElement) {
      if (targetElement.type === 'radio') {
        const radioGroup = this.querySelectorAll(`input[name="${targetElement.name}"]`);
        radioGroup.forEach(radio => {
          if (radio instanceof HTMLInputElement) {
            radio.checked = false;
          }
        });
      }
      targetElement.checked = true;
    }
  }

  /**
   * Updates product images when variant changes
   * @param {Document} newHtml - The new HTML document
   */
  updateProductImages(newHtml) {
    const productCard = this.closest('product-card');

    if (productCard) {
      const currentMedia = productCard.querySelector('product-media, .product-media, [class*="media"]');
      const newMedia = newHtml.querySelector('product-media, .product-media, [class*="media"]');
      if (currentMedia && newMedia) {
        morph(currentMedia, newMedia);
      }
    }
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}